import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { NativeSelect } from '../../components/ui/native-select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  BookOpen, Plus, Search, RefreshCcw, Edit, Trash2, 
  Eye, GraduationCap, Clock, Layers, FileText, Users,
  CheckCircle, XCircle, ChevronRight, Upload, Video,
  Link as LinkIcon, Download, BarChart, UserPlus, X, UsersRound
} from 'lucide-react';
import { toast } from 'sonner';

const CourseManagementPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterPublished, setFilterPublished] = useState('all');
  const [trainers, setTrainers] = useState([]);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showTrainersDialog, setShowTrainersDialog] = useState(false);
  const [showStudentsDialog, setShowStudentsDialog] = useState(false);
  const [showGroupsDialog, setShowGroupsDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  
  // Trainer assignment state
  const [courseTrainers, setCourseTrainers] = useState([]);
  const [availableTrainers, setAvailableTrainers] = useState([]);
  const [selectedTrainerIds, setSelectedTrainerIds] = useState([]);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  
  // Student enrollment state
  const [courseStudents, setCourseStudents] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  
  // Group enrollment state
  const [courseGroups, setCourseGroups] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cover_image_url: '',
    duration_hours: '',
    level: '',
    category: '',
    trainer_id: '',
    is_published: false
  });

  const fetchCourses = useCallback(async () => {
    try {
      const response = await api.get('/content/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Erreur lors du chargement des cours');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTrainers = useCallback(async () => {
    try {
      const response = await api.get('/trainers');
      setTrainers(response.data);
    } catch (error) {
      console.error('Error fetching trainers:', error);
    }
  }, []);

  // Fetch trainers assigned to a course
  const fetchCourseTrainers = useCallback(async (courseId) => {
    setLoadingTrainers(true);
    try {
      const [assignedRes, availableRes] = await Promise.all([
        api.get(`/courses/${courseId}/trainers`),
        api.get(`/courses/${courseId}/available-trainers`)
      ]);
      setCourseTrainers(assignedRes.data);
      setAvailableTrainers(availableRes.data);
      setSelectedTrainerIds([]);
    } catch (error) {
      console.error('Error fetching course trainers:', error);
      toast.error('Erreur lors du chargement des formateurs');
    } finally {
      setLoadingTrainers(false);
    }
  }, []);

  // Open trainer assignment dialog
  const openTrainersDialog = (course) => {
    setSelectedCourse(course);
    setShowTrainersDialog(true);
    fetchCourseTrainers(course.id);
  };

  // Add selected trainers to course
  const handleAddTrainers = async () => {
    if (!selectedCourse || selectedTrainerIds.length === 0) return;
    
    try {
      await api.post(`/courses/${selectedCourse.id}/trainers`, {
        trainer_ids: selectedTrainerIds
      });
      toast.success(`${selectedTrainerIds.length} formateur(s) ajouté(s)`);
      fetchCourseTrainers(selectedCourse.id);
      fetchCourses(); // Refresh course list
    } catch (error) {
      toast.error('Erreur lors de l\'ajout des formateurs');
    }
  };

  // Remove trainer from course
  const handleRemoveTrainer = async (trainerId) => {
    if (!selectedCourse) return;
    
    try {
      await api.delete(`/courses/${selectedCourse.id}/trainers/${trainerId}`);
      toast.success('Formateur retiré du cours');
      fetchCourseTrainers(selectedCourse.id);
      fetchCourses(); // Refresh course list
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Toggle trainer selection
  const toggleTrainerSelection = (trainerId) => {
    setSelectedTrainerIds(prev => 
      prev.includes(trainerId)
        ? prev.filter(id => id !== trainerId)
        : [...prev, trainerId]
    );
  };

  // ========== Student Enrollment Management ==========
  
  // Fetch students enrolled in a course
  const fetchCourseStudents = useCallback(async (courseId) => {
    setLoadingStudents(true);
    try {
      const [enrolledRes, availableRes] = await Promise.all([
        api.get(`/enrollments/course/${courseId}`),
        api.get(`/enrollments/available-students/${courseId}`)
      ]);
      setCourseStudents(enrolledRes.data);
      setAvailableStudents(availableRes.data);
      setSelectedStudentIds([]);
    } catch (error) {
      console.error('Error fetching course students:', error);
      toast.error('Erreur lors du chargement des étudiants');
    } finally {
      setLoadingStudents(false);
    }
  }, []);

  // Open student enrollment dialog
  const openStudentsDialog = (course) => {
    setSelectedCourse(course);
    setShowStudentsDialog(true);
    fetchCourseStudents(course.id);
  };

  // Add selected students to course
  const handleAddStudents = async () => {
    if (!selectedCourse || selectedStudentIds.length === 0) return;
    
    try {
      await api.post('/enrollments/bulk', {
        student_ids: selectedStudentIds,
        course_id: selectedCourse.id
      });
      toast.success(`${selectedStudentIds.length} étudiant(s) inscrit(s)`);
      fetchCourseStudents(selectedCourse.id);
    } catch (error) {
      toast.error('Erreur lors de l\'inscription des étudiants');
    }
  };

  // Remove student from course
  const handleRemoveStudent = async (enrollmentId) => {
    try {
      await api.delete(`/enrollments/${enrollmentId}`);
      toast.success('Étudiant désinscrit du cours');
      fetchCourseStudents(selectedCourse.id);
    } catch (error) {
      toast.error('Erreur lors de la désinscription');
    }
  };

  // Toggle student selection
  const toggleStudentSelection = (studentId) => {
    setSelectedStudentIds(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  // ========== Group Enrollment Management ==========
  
  // Fetch groups for a course
  const fetchCourseGroups = useCallback(async (courseId) => {
    setLoadingGroups(true);
    try {
      const [linkedRes, availableRes] = await Promise.all([
        api.get(`/enrollments/groups/course/${courseId}`),
        api.get(`/enrollments/available-groups/${courseId}`)
      ]);
      setCourseGroups(linkedRes.data);
      setAvailableGroups(availableRes.data);
      setSelectedGroupId(null);
    } catch (error) {
      console.error('Error fetching course groups:', error);
      toast.error('Erreur lors du chargement des groupes');
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  // Open groups dialog
  const openGroupsDialog = (course) => {
    setSelectedCourse(course);
    setShowGroupsDialog(true);
    fetchCourseGroups(course.id);
  };

  // Add group to course
  const handleAddGroup = async () => {
    if (!selectedCourse || !selectedGroupId) return;
    
    try {
      const response = await api.post('/enrollments/group', {
        group_id: selectedGroupId,
        course_id: selectedCourse.id
      });
      toast.success(response.data.message);
      fetchCourseGroups(selectedCourse.id);
      fetchCourses(); // Refresh course list to update enrolled count
    } catch (error) {
      toast.error('Erreur lors de l\'affectation du groupe');
    }
  };

  useEffect(() => {
    fetchCourses();
    fetchTrainers();
  }, [fetchCourses, fetchTrainers]);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom du cours est requis');
      return;
    }
    if (formData.is_published) {
      toast.error(
        'Impossible de publier à la création. Ajoutez un formateur et du contenu, puis publiez.'
      );
      return;
    }

    try {
      const response = await api.post('/content/courses', {
        name: formData.name,
        description: formData.description,
        cover_image_url: formData.cover_image_url || null,
        duration_hours: formData.duration_hours ? parseInt(formData.duration_hours) : null,
        level: formData.level || null,
        category: formData.category || null,
        trainer_id: formData.trainer_id && formData.trainer_id !== 'none' ? parseInt(formData.trainer_id) : null,
        is_published: false
      });
      toast.success('Cours créé avec succès');
      setShowCreateDialog(false);
      resetForm();
      fetchCourses();
      // Navigate to course editor
      navigate(`/admin/courses/${response.data.id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdate = async () => {
    if (formData.is_published && selectedCourse && !selectedCourse.is_published) {
      const blockers = getPublishBlockers({
        ...selectedCourse,
        trainer_id:
          formData.trainer_id && formData.trainer_id !== 'none'
            ? parseInt(formData.trainer_id, 10)
            : selectedCourse.trainer_id,
        has_assigned_trainer:
          selectedCourse.has_assigned_trainer
          || (formData.trainer_id && formData.trainer_id !== 'none'),
      });
      if (blockers.length) {
        toast.error(`Impossible de publier. Requis : ${blockers.join(', ')}.`);
        return;
      }
    }
    try {
      await api.put(`/content/courses/${selectedCourse.id}`, {
        name: formData.name,
        description: formData.description,
        cover_image_url: formData.cover_image_url || null,
        duration_hours: formData.duration_hours ? parseInt(formData.duration_hours) : null,
        level: formData.level || null,
        category: formData.category || null,
        trainer_id: formData.trainer_id && formData.trainer_id !== 'none' ? parseInt(formData.trainer_id) : null,
        is_published: formData.is_published
      });
      toast.success('Cours mis à jour');
      setShowEditDialog(false);
      resetForm();
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce cours et tout son contenu ?')) return;

    try {
      await api.delete(`/content/courses/${courseId}`);
      toast.success('Cours supprimé');
      fetchCourses();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getPublishBlockers = (course) => {
    const blockers = [];
    const hasTrainer = !!(course.has_assigned_trainer || course.trainer_id);
    const hasContent = (course.module_count || 0) > 0 && (course.lesson_count || 0) > 0;
    if (!hasTrainer) blockers.push('au moins un formateur assigné');
    if (!hasContent) blockers.push('au moins un module contenant une leçon');
    return blockers;
  };

  const handleTogglePublish = async (course) => {
    const nextPublished = !course.is_published;
    if (nextPublished) {
      const blockers = getPublishBlockers(course);
      if (blockers.length) {
        toast.error(`Impossible de publier. Requis : ${blockers.join(', ')}.`);
        return;
      }
    }
    try {
      await api.put(`/content/courses/${course.id}`, {
        is_published: nextPublished
      });
      toast.success(nextPublished ? 'Cours publié' : 'Cours dépublié');
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const openEdit = (course) => {
    setSelectedCourse(course);
    setFormData({
      name: course.name,
      description: course.description || '',
      cover_image_url: course.cover_image_url || '',
      duration_hours: course.duration_hours?.toString() || '',
      level: course.level || '',
      category: course.category || '',
      trainer_id: course.trainer_id?.toString() || '',
      is_published: course.is_published
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      cover_image_url: '',
      duration_hours: '',
      level: '',
      category: '',
      trainer_id: '',
      is_published: false
    });
    setSelectedCourse(null);
  };

  // Filter courses
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLevel = filterLevel === 'all' || course.level === filterLevel;
    const matchesPublished = filterPublished === 'all' || 
      (filterPublished === 'published' && course.is_published) ||
      (filterPublished === 'draft' && !course.is_published);
    return matchesSearch && matchesLevel && matchesPublished;
  });

  // Stats
  const stats = {
    total: courses.length,
    published: courses.filter(c => c.is_published).length,
    totalModules: courses.reduce((sum, c) => sum + (c.module_count || 0), 0),
    totalLessons: courses.reduce((sum, c) => sum + (c.lesson_count || 0), 0)
  };

  const getLevelBadge = (level) => {
    const levels = {
      beginner: { label: 'Débutant', class: 'bg-emerald-100 text-emerald-700' },
      intermediate: { label: 'Intermédiaire', class: 'bg-blue-100 text-blue-700' },
      advanced: { label: 'Avancé', class: 'bg-violet-100 text-violet-700' }
    };
    // No badge when no level was chosen
    return levels[level] || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Gestion des Cours" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Total cours</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <BookOpen className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Publiés</p>
                  <p className="text-2xl font-bold">{stats.published}</p>
                </div>
                <CheckCircle className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Modules</p>
                  <p className="text-2xl font-bold">{stats.totalModules}</p>
                </div>
                <Layers className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Leçons</p>
                  <p className="text-2xl font-bold">{stats.totalLessons}</p>
                </div>
                <FileText className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un cours..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-48"
                data-testid="search-courses"
              />
            </div>
            
            <NativeSelect
              value={filterLevel}
              onChange={setFilterLevel}
              options={[
                { value: 'all', label: 'Tous niveaux' },
                { value: 'beginner', label: 'Débutant' },
                { value: 'intermediate', label: 'Intermédiaire' },
                { value: 'advanced', label: 'Avancé' }
              ]}
              className="w-36"
              data-testid="filter-level"
            />
            
            <NativeSelect
              value={filterPublished}
              onChange={setFilterPublished}
              options={[
                { value: 'all', label: 'Tous' },
                { value: 'published', label: 'Publiés' },
                { value: 'draft', label: 'Brouillons' }
              ]}
              className="w-32"
              data-testid="filter-published"
            />
            
            <Button variant="outline" size="icon" onClick={fetchCourses}>
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
          
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="create-course-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Cours
          </Button>
        </div>

        {/* Courses Grid */}
        {filteredCourses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map(course => {
              const levelBadge = getLevelBadge(course.level);
              return (
                <Card 
                  key={course.id} 
                  className="hover:shadow-lg transition-shadow overflow-hidden"
                  data-testid={`course-card-${course.id}`}
                >
                  {/* Cover Image */}
                  <div className="h-32 bg-gradient-to-br from-violet-500 to-purple-600 relative">
                    {course.cover_image_url && (
                      <img 
                        src={course.cover_image_url} 
                        alt={course.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute top-2 right-2 flex gap-1">
                      <Badge className={course.is_published 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-slate-500 text-white'
                      }>
                        {course.is_published ? 'Publié' : 'Brouillon'}
                      </Badge>
                    </div>
                  </div>
                  
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-lg text-slate-900 dark:text-white line-clamp-1">
                        {course.name}
                      </h3>
                      {levelBadge && (
                        <Badge className={levelBadge.class}>
                          {levelBadge.label}
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
                      {course.description || 'Aucune description'}
                    </p>
                    
                    {/* Info Grid */}
                    <div className="grid grid-cols-4 gap-2 mb-4 text-center">
                      <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                        <p className="text-lg font-bold text-violet-600">{course.module_count || 0}</p>
                        <p className="text-xs text-slate-500">Modules</p>
                      </div>
                      <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                        <p className="text-lg font-bold text-blue-600">{course.lesson_count || 0}</p>
                        <p className="text-xs text-slate-500">Leçons</p>
                      </div>
                      <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                        <p className="text-sm font-bold text-emerald-600">
                          {course.duration_calculated?.total_minutes > 0
                            ? course.duration_calculated.formatted
                            : `${course.duration_hours || 0}h`}
                        </p>
                        <p className="text-xs text-slate-500">Durée</p>
                      </div>
                      <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                        <p className="text-lg font-bold text-orange-600">{course.enrolled_count || 0}</p>
                        <p className="text-xs text-slate-500">Élèves</p>
                      </div>
                    </div>
                    
                    {course.trainer_name && (
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                        <GraduationCap className="w-4 h-4" />
                        <span>{course.trainer_name}</span>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        className="flex-1 bg-violet-600 hover:bg-violet-700"
                        onClick={() => navigate(`/admin/courses/${course.id}/edit`)}
                        data-testid={`edit-content-${course.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Contenu
                      </Button>
                      <Button 
                        variant="outline"
                        size="icon"
                        onClick={() => openGroupsDialog(course)}
                        title="Gérer les classes/groupes"
                        data-testid={`manage-groups-${course.id}`}
                      >
                        <UsersRound className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline"
                        size="icon"
                        onClick={() => openStudentsDialog(course)}
                        title="Gérer les étudiants inscrits"
                        data-testid={`manage-students-${course.id}`}
                      >
                        <Users className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline"
                        size="icon"
                        onClick={() => openTrainersDialog(course)}
                        title="Gérer les formateurs"
                        data-testid={`manage-trainers-${course.id}`}
                      >
                        <UserPlus className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline"
                        size="icon"
                        onClick={() => navigate(`/admin/courses/${course.id}/analytics`)}
                        title="Analytics"
                        data-testid={`analytics-${course.id}`}
                      >
                        <BarChart className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline"
                        size="icon"
                        onClick={() => handleTogglePublish(course)}
                        disabled={!course.is_published && getPublishBlockers(course).length > 0}
                        title={
                          !course.is_published && getPublishBlockers(course).length > 0
                            ? `Requis : ${getPublishBlockers(course).join(', ')}`
                            : (course.is_published ? 'Dépublier' : 'Publier')
                        }
                        data-testid={`toggle-publish-${course.id}`}
                      >
                        {course.is_published ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="outline"
                        size="icon"
                        onClick={() => openEdit(course)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(course.id)}
                        data-testid={`delete-course-${course.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                {searchTerm ? 'Aucun cours trouvé' : 'Aucun cours créé'}
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Créer votre premier cours
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Course Dialog — flex + sticky footer so submit stays reachable on mobile */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90dvh] flex flex-col overflow-hidden gap-0 p-0">
          <DialogHeader className="px-4 pt-5 pb-3 sm:px-6 shrink-0 border-b">
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-violet-600" />
              Nouveau Cours
            </DialogTitle>
            <DialogDescription>
              Créez un nouveau cours pour votre centre de formation
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-6 space-y-4">
            <div>
              <Label>Nom du cours *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Développement Web Full Stack"
                data-testid="course-name-input"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description du cours..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Niveau</Label>
                <NativeSelect
                  value={formData.level}
                  onChange={(v) => setFormData({ ...formData, level: v })}
                  options={[
                    { value: '', label: 'Non défini' },
                    { value: 'beginner', label: 'Débutant' },
                    { value: 'intermediate', label: 'Intermédiaire' },
                    { value: 'advanced', label: 'Avancé' }
                  ]}
                />
              </div>
              
              <div>
                <Label>Durée (heures)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.duration_hours}
                  onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                  placeholder="Ex: 40"
                />
              </div>
            </div>
            
            <div>
              <Label>Catégorie</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="Ex: Développement, Marketing, Design..."
              />
            </div>
            
            <div>
              <Label>Formateur principal</Label>
              <NativeSelect
                value={formData.trainer_id}
                onChange={(v) => setFormData({ ...formData, trainer_id: v })}
                options={[
                  { value: '', label: 'Aucun formateur' },
                  ...trainers.map(trainer => ({ 
                    value: trainer.id.toString(), 
                    label: `${trainer.first_name} ${trainer.last_name}` 
                  }))
                ]}
                placeholder="Sélectionner un formateur"
              />
              <p className="text-xs text-slate-500 mt-1">
                Vous pourrez ajouter d'autres formateurs après la création
              </p>
            </div>
            
            <div>
              <Label>URL de l'image de couverture</Label>
              <Input
                value={formData.cover_image_url}
                onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
          
          <DialogFooter className="px-4 py-4 sm:px-6 shrink-0 border-t bg-background sticky bottom-0">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => { setShowCreateDialog(false); resetForm(); }}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleCreate}
              className="w-full sm:w-auto bg-violet-600 hover:bg-violet-700"
              data-testid="submit-create-course"
            >
              Créer et éditer le contenu
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Course Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-violet-600" />
              Modifier le cours
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom du cours *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Niveau</Label>
                <NativeSelect
                  value={formData.level}
                  onChange={(v) => setFormData({ ...formData, level: v })}
                  options={[
                    { value: '', label: 'Non défini' },
                    { value: 'beginner', label: 'Débutant' },
                    { value: 'intermediate', label: 'Intermédiaire' },
                    { value: 'advanced', label: 'Avancé' }
                  ]}
                />
              </div>
              
              <div>
                <Label>Durée (heures)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.duration_hours}
                  onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Catégorie</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Formateur principal</Label>
              <NativeSelect
                value={formData.trainer_id}
                onChange={(v) => setFormData({ ...formData, trainer_id: v })}
                options={[
                  { value: '', label: 'Aucun formateur' },
                  ...trainers.map(trainer => ({ 
                    value: trainer.id.toString(), 
                    label: `${trainer.first_name} ${trainer.last_name}` 
                  }))
                ]}
                placeholder="Sélectionner un formateur"
              />
            </div>
            
            <div>
              <Label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="w-4 h-4"
                />
                Publier le cours (visible pour les étudiants)
              </Label>
              <p className="text-xs text-slate-500 mt-1">
                Requis : formateur assigné et au moins un module avec une leçon.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); resetForm(); }}>
              Annuler
            </Button>
            <Button 
              onClick={handleUpdate}
              className="bg-violet-600 hover:bg-violet-700"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trainers Assignment Dialog */}
      <Dialog open={showTrainersDialog} onOpenChange={setShowTrainersDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-violet-600" />
              Gérer les formateurs
            </DialogTitle>
            <DialogDescription>
              {selectedCourse?.name} - Affectez un ou plusieurs formateurs à ce cours
            </DialogDescription>
          </DialogHeader>
          
          {loadingTrainers ? (
            <div className="flex justify-center py-8">
              <RefreshCcw className="w-6 h-6 animate-spin text-violet-600" />
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Currently assigned trainers */}
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-violet-600" />
                  Formateurs assignés ({courseTrainers.length})
                </h4>
                
                {courseTrainers.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 dark:bg-slate-800 rounded-lg">
                    Aucun formateur assigné à ce cours
                  </p>
                ) : (
                  <div className="space-y-2">
                    {courseTrainers.map(trainer => (
                      <div 
                        key={trainer.id}
                        className="flex items-center justify-between p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center text-white font-medium">
                            {trainer.first_name?.[0]}{trainer.last_name?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {trainer.first_name} {trainer.last_name}
                            </p>
                            <p className="text-sm text-slate-500">{trainer.email}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveTrainer(trainer.id)}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Retirer
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Available trainers to add */}
              {availableTrainers.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-emerald-600" />
                    Formateurs disponibles ({availableTrainers.length})
                  </h4>
                  
                  <div className="max-h-60 overflow-y-auto space-y-2 border border-slate-200 dark:border-slate-700 rounded-lg p-2">
                    {availableTrainers.map(trainer => (
                      <label
                        key={trainer.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedTrainerIds.includes(trainer.id)
                            ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                            : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTrainerIds.includes(trainer.id)}
                          onChange={() => toggleTrainerSelection(trainer.id)}
                          className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <div className="w-10 h-10 rounded-full bg-slate-400 flex items-center justify-center text-white font-medium">
                          {trainer.first_name?.[0]}{trainer.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {trainer.first_name} {trainer.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{trainer.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  
                  {selectedTrainerIds.length > 0 && (
                    <Button
                      onClick={handleAddTrainers}
                      className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter {selectedTrainerIds.length} formateur(s)
                    </Button>
                  )}
                </div>
              )}
              
              {availableTrainers.length === 0 && courseTrainers.length > 0 && (
                <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  Tous les formateurs sont déjà assignés à ce cours
                </p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTrainersDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Students Enrollment Dialog */}
      <Dialog open={showStudentsDialog} onOpenChange={setShowStudentsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Gérer les étudiants
            </DialogTitle>
            <DialogDescription>
              {selectedCourse?.name} - Inscrivez des étudiants à ce cours
            </DialogDescription>
          </DialogHeader>
          
          {loadingStudents ? (
            <div className="flex justify-center py-8">
              <RefreshCcw className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Currently enrolled students */}
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-emerald-600" />
                  Étudiants inscrits ({courseStudents.length})
                </h4>
                
                {courseStudents.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 dark:bg-slate-800 rounded-lg">
                    Aucun étudiant inscrit à ce cours
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {courseStudents.map(student => (
                      <div 
                        key={student.id}
                        className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-medium">
                            {student.student_name?.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {student.student_name}
                            </p>
                            <p className="text-sm text-slate-500">{student.student_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                            {student.progress_percent}% complété
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveStudent(student.id)}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Retirer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Available students to add */}
              {availableStudents.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    Étudiants disponibles ({availableStudents.length})
                  </h4>
                  
                  <div className="max-h-60 overflow-y-auto space-y-2 border border-slate-200 dark:border-slate-700 rounded-lg p-2">
                    {availableStudents.map(student => (
                      <label
                        key={student.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedStudentIds.includes(student.id)
                            ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                            : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedStudentIds.includes(student.id)}
                          onChange={() => toggleStudentSelection(student.id)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="w-10 h-10 rounded-full bg-slate-400 flex items-center justify-center text-white font-medium">
                          {student.first_name?.[0]}{student.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {student.first_name} {student.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{student.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  
                  {selectedStudentIds.length > 0 && (
                    <Button
                      onClick={handleAddStudents}
                      className="w-full mt-3 bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Inscrire {selectedStudentIds.length} étudiant(s)
                    </Button>
                  )}
                </div>
              )}
              
              {availableStudents.length === 0 && courseStudents.length > 0 && (
                <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  Tous les étudiants sont déjà inscrits à ce cours
                </p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStudentsDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Groups Dialog */}
      <Dialog open={showGroupsDialog} onOpenChange={setShowGroupsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersRound className="w-5 h-5 text-purple-600" />
              Gérer les classes / groupes
            </DialogTitle>
            <DialogDescription>
              {selectedCourse?.name} - Affectez des classes à ce cours
            </DialogDescription>
          </DialogHeader>
          
          {loadingGroups ? (
            <div className="flex justify-center py-8">
              <RefreshCcw className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Currently linked groups */}
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  <UsersRound className="w-4 h-4 text-purple-600" />
                  Classes affectées ({courseGroups.length})
                </h4>
                
                {courseGroups.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 dark:bg-slate-800 rounded-lg">
                    Aucune classe affectée à ce cours
                  </p>
                ) : (
                  <div className="space-y-2">
                    {courseGroups.map(group => (
                      <div 
                        key={group.id}
                        className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center text-white font-medium">
                            <UsersRound className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {group.name}
                            </p>
                            <p className="text-sm text-slate-500">
                              {group.member_count} élèves
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Available groups */}
              {availableGroups.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                    <Plus className="w-4 h-4 text-slate-600" />
                    Classes disponibles ({availableGroups.length})
                  </h4>
                  
                  <div className="space-y-2">
                    {availableGroups.map(group => (
                      <label
                        key={group.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedGroupId === group.id
                            ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800'
                            : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        <input
                          type="radio"
                          name="group"
                          checked={selectedGroupId === group.id}
                          onChange={() => setSelectedGroupId(group.id)}
                          className="w-4 h-4 border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <div className="w-10 h-10 rounded-lg bg-slate-400 flex items-center justify-center text-white">
                          <UsersRound className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {group.name}
                          </p>
                          <p className="text-sm text-slate-500">{group.member_count} élèves</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  
                  {selectedGroupId && (
                    <Button
                      onClick={handleAddGroup}
                      className="w-full mt-3 bg-purple-600 hover:bg-purple-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Affecter cette classe au cours
                    </Button>
                  )}
                </div>
              )}
              
              {availableGroups.length === 0 && courseGroups.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500 mb-2">
                    Aucune classe disponible
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin/groups')}
                    className="text-purple-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Créer une classe
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupsDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseManagementPage;
